import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TpRectangleCard from "./components/TpRectangleCard";
import { getContentByFormat, getContentCounts, likeContent, bookmarkContent, getLikeBookmarkedContent } from "./service";
import useStore from "../../store/useStore";
import { getDecodedToken, getLocalStorage } from "../../utils/localStorage";
import { AUTH_TOKEN } from "../../utils/constants";
import backIcon from "../../assets/images/arrow-left.svg";

interface FormatContentListProps {}

interface ContentCounts {
  [key: string]: {
    isExternalSharable: number;
    like_count: number;
    uploadThumbnailUrl: string;
    previewUrl: string;
    readTime: string;
    duration: string;
  };
}

const FormatContentList: React.FC<FormatContentListProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [content, setContent] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [localLikes, setLocalLikes] = useState<number[]>([]);
  const [localBookmarks, setLocalBookmarks] = useState<number[]>([]);
  const [contentCounts, setContentCounts] = useState<ContentCounts>({});
  const [isActionLoading, setIsActionLoading] = useState(false);

  // Get store values
  const {
    tatvaPediaToken,
    tatvaPediaBookmarks,
    updateTatvaPediaBookmark,
    updateTatvaPediaLike,
    setTatvaPediaBookmarkedList,
    setTatvaPediaLikedList,
    pediaUserPreferences,
  } = useStore();

  // Get user details from token
  const tokenData: any = getDecodedToken();
  const doctorDetails: any = tokenData?.user || {};

  // Extract parameters from URL
  const params = new URLSearchParams(location.search);
  const contentType = params.get("contentType");
  const contentTypeId = params.get("contentTypeId");
  const type = params.get("type"); // videos, documents, podcasts, slides
  const previousTab = params.get("previousTab");

  // Extract speciality IDs from user preferences
  const specialityIds = useMemo(() => {
    const extractSpecialityIds = (specialitiesString: string) => {
      if (!specialitiesString) return [];
      
      const match = specialitiesString.match(/speciality_id:\((.*?)\)/);
      if (!match || !match[1]) return [];
      
      return match[1].split("OR").map((id) => parseInt(id.trim()));
    };

    return extractSpecialityIds(pediaUserPreferences?.specialities);
  }, [pediaUserPreferences?.specialities]);

  // Map format type to contentFormatId
  const getContentFormatId = (formatType: string) => {
    switch (formatType?.toLowerCase()) {
      case "videos":
        return 1000;
      case "documents":
        return 1001;
      case "podcasts":
        return 1002;
      case "slides":
        return 1003;
      default:
        return 1001; // Default to documents
    }
  };

  // Get display title
  const getDisplayTitle = (formatType: string) => {
    switch (formatType?.toLowerCase()) {
      case "videos":
        return "Videos";
      case "documents":
        return "Documents";
      case "podcasts":
        return "Podcasts";
      case "slides":
        return "Slides";
      default:
        return "Content";
    }
  };

  // Fetch content
  const fetchContent = async (pageNum: number = 0, append: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const contentFormatId = getContentFormatId(type || "");
      const consolidatedModuleId = contentTypeId ? parseInt(contentTypeId) : 6002;

      const response = await getContentByFormat({
        consolidatedModuleId,
        contentFormatId,
        specialityIds,
        page: pageNum,
        pageSize: 10,
      });

      if (response?.response?.docs) {
        const transformedData = response.response.docs.map((doc: any) => ({
          id: doc.id,
          docId: doc.docId,
          contentTitle: doc.contentTitle,
          contentType: type || "documents", // Use the format type for icon display
          contentTypeId: doc.contentTypeId,
          contentDescription: doc.contentDescription,
          uploadThumbnailUrl: doc.uploadThumbnailUrl,
          coverPhotoUrl: "",
          duration: doc.duration,
          isLike: false,
          isBookmarked: false,
          viewCount: doc.view_count || 0,
          like_count: doc.like_count || 0,
          shareCount: doc.share_count || 0,
          contentGenreId: doc.contentGenreId || [],
          genres: [],
          publishedDate: doc.publishedDate,
          deepLinkCode: doc.deepLinkCode,
          consolidatedModule: doc.consolidatedModule || "",
        }));

        if (append) {
          setContent(prev => [...prev, ...transformedData]);
        } else {
          setContent(transformedData);
        }

        setHasMore(transformedData.length === 10);
        setPage(pageNum);
      } else {
        setContent([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching content:", error);
      setError("Failed to fetch content");
    } finally {
      setLoading(false);
    }
  };

  // Fetch content counts
  const fetchContentCounts = async () => {
    if (!content.length) return;

    try {
      const token = getToken();
      if (!token) return;

      const contents = content.map((doc: any) => ({
        contentId: doc.docId,
        contentTypeId: doc.contentTypeId,
      }));

      const countsResponse = await getContentCounts({
        contents,
        token,
        userId: doctorDetails?.user?.id,
      });

      if (countsResponse?.body) {
        const countsMap = countsResponse.body.reduce((acc: any, item: any) => {
          const key = `${item.docId}_${item.docTypeId}`;
          acc[key] = {
            like_count: item.likeCount,
            isExternalSharable: item.isExternalSharable,
            uploadThumbnailUrl: item.uploadThumbnailUrl,
            previewUrl: item.previewUrl,
            readTime: item.readTime,
            duration: item.duration,
          };
          return acc;
        }, {});

        setContentCounts(countsMap);
      }
    } catch (error) {
      console.error("Error fetching content counts:", error);
    }
  };

  // Initialize liked and bookmarked content
  useEffect(() => {
    const fetchInitialState = async () => {
      if (!tatvaPediaToken) return;

      try {
        const [likedResponse, bookmarkedResponse] = await Promise.all([
          getLikeBookmarkedContent("liked", tatvaPediaToken),
          getLikeBookmarkedContent("bookmarked", tatvaPediaToken),
        ]);

        if (likedResponse) {
          setTatvaPediaLikedList(likedResponse);
          const likedIds = likedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalLikes(likedIds);
        }

        if (bookmarkedResponse) {
          setTatvaPediaBookmarkedList(bookmarkedResponse);
          const bookmarkedIds = bookmarkedResponse
            .filter((item: any) => item && item.contentId)
            .map((item: any) => item.contentId);
          setLocalBookmarks(bookmarkedIds);
        }
      } catch (error) {
        console.error("Error fetching initial state:", error);
      }
    };

    fetchInitialState();
  }, [tatvaPediaToken]);

  // Fetch content on mount and when dependencies change
  useEffect(() => {
    if (type && contentTypeId) {
      fetchContent(0, false);
    }
  }, [type, contentTypeId, specialityIds]);

  // Fetch content counts when content changes
  useEffect(() => {
    fetchContentCounts();
  }, [content]);

  // Handle like
  const handleLike = async (content: any, isLiked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);

      // Optimistically update UI
      setLocalLikes((prev) => {
        const newLikes = isLiked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newLikes;
      });

      // Update content counts
      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? -1 : 1),
          },
        };
      });

      // Make API call
      const response = await likeContent(
        content.docId,
        content.contentTypeId,
        !isLiked,
        token
      );

      if (response) {
        updateTatvaPediaLike(content, !isLiked);

        const updatedLikedContent = await getLikeBookmarkedContent("liked", token);
        if (updatedLikedContent) {
          setTatvaPediaLikedList(updatedLikedContent);
        }
      } else {
        // Revert on failure
        setLocalLikes((prev) =>
          isLiked ? [...prev, content.docId] : prev.filter((id) => id !== content.docId)
        );

        setContentCounts((prev) => {
          const key = `${content.docId}_${content.contentTypeId}`;
          const currentCount = prev[key] || { like_count: 0 };
          return {
            ...prev,
            [key]: {
              ...currentCount,
              like_count: currentCount.like_count + (isLiked ? 1 : -1),
            },
          };
        });
      }
    } catch (error) {
      console.error("Error in like operation:", error);
      // Revert on error
      setLocalLikes((prev) =>
        isLiked ? [...prev, content.docId] : prev.filter((id) => id !== content.docId)
      );

      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? 1 : -1),
          },
        };
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  // Handle bookmark
  const handleBookmark = async (content: any, isBookmarked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);

      // Optimistically update UI
      setLocalBookmarks((prev) => {
        const newBookmarks = isBookmarked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newBookmarks;
      });

      // Make API call
      const response = await bookmarkContent(
        content.docId,
        content.contentTypeId,
        !isBookmarked,
        token
      );

      if (response) {
        updateTatvaPediaBookmark(content, !isBookmarked);

        const updatedBookmarkedContent = await getLikeBookmarkedContent("bookmarked", token);
        if (updatedBookmarkedContent) {
          setTatvaPediaBookmarkedList(updatedBookmarkedContent);
        }
      } else {
        // Revert on failure
        setLocalBookmarks((prev) =>
          isBookmarked ? [...prev, content.docId] : prev.filter((id) => id !== content.docId)
        );
      }
    } catch (error) {
      console.error("Error in bookmark operation:", error);
      // Revert on error
      setLocalBookmarks((prev) =>
        isBookmarked ? [...prev, content.docId] : prev.filter((id) => id !== content.docId)
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  // Check if item is liked
  const isItemLiked = (item: any) => {
    if (!item?.docId) return false;
    return localLikes.includes(item.docId);
  };

  // Check if item is bookmarked
  const isItemBookmarked = (item: any) => {
    if (!item?.docId) return false;
    return localBookmarks.includes(item.docId);
  };

  // Helper function to get token
  const getToken = () => tatvaPediaToken || localStorage.getItem("tatvaPediaToken");

  // Handle load more
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchContent(page + 1, true);
    }
  };

  // Handle back navigation
  const handleBackClick = () => {
    if (previousTab) {
      navigate(`/tatvapedia?activeTab=${encodeURIComponent(previousTab)}`);
    } else {
      navigate(-1);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={handleBackClick}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackClick}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <img src={backIcon} alt="Back" className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              {getDisplayTitle(type || "")}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading && page === 0 ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse bg-gray-200 rounded-xl h-32 w-full"
              ></div>
            ))}
          </div>
        ) : content.length > 0 ? (
          <div className="space-y-4">
            {content.map((item: any) => {
              const countKey = `${item.docId}_${item.contentTypeId}`;
              const currentCounts = contentCounts[countKey] || {};

              return (
                <TpRectangleCard
                  key={item.id}
                  isShowcontentType={true}
                  isLike={isItemLiked(item)}
                  isBookmarked={isItemBookmarked(item)}
                  onLike={() => handleLike(item, isItemLiked(item))}
                  onBookmark={() => handleBookmark(item, isItemBookmarked(item))}
                  data={{
                    docId: item.docId,
                    contentTitle: item.contentTitle,
                    contentType: item.contentType,
                    uploadThumbnailUrl: item.uploadThumbnailUrl,
                    contentDescription: item.contentDescription,
                    previewDeepLink: item.previewDeepLink,
                    like_count: currentCounts.like_count || item.like_count || 0,
                    contentTypeId: item.contentTypeId,
                    publishedDate: item.publishedDate,
                    isExternalSharable: currentCounts.isExternalSharable || 0,
                    previewUrl: currentCounts.previewUrl || "",
                    readTime: currentCounts.readTime || "",
                    duration: currentCounts.duration || "",
                    deepLinkCode: item.deepLinkCode,
                    consolidatedModule: item.consolidatedModule || "",
                    currentTab: previousTab || undefined,
                    source: window.location.pathname + window.location.search,
                  }}
                />
              );
            })}

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center pt-4">
                <button
                  onClick={handleLoadMore}
                  disabled={loading}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {loading ? "Loading..." : "Load More"}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No content available</p>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {isActionLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      )}
    </div>
  );
};

export default FormatContentList; 